/**
 * The permissions folder has utilities and components for handling permission related rendering.
 * - `permissions.tsx` file relates to checking if you are allowed to do something
 * - `permission-keys.tsx` file relates to the `resource.action` combinations. Can you do `action` on `resource`?
 * - `permission-messages.tsx` file relates to the `reason` and `messaging` related to permissions.
 *   - What is the internal `reason` why you can't do something and what `messaging` is displayed to the user.
 */

import { CircularProgress, Tooltip } from "@mui/material";
import { Reason } from "data/models";
import { PermissionsKey } from "./permission-keys";
import { usePermissions } from "./usePermissions";

interface PermissionsWrapperProps {
  keys: PermissionsKey[]; // User must be allowed to complete all of these actions on all resources to proceed
  messages?: Partial<Record<Reason, React.ReactNode>>; // A default set of messages are shown to the user for each reason but these can be overridden.
  children?: React.ReactNode; // The children are rendered if permissionKeys is allowed
}

export const PermissionsWrapper = (props: PermissionsWrapperProps) => {
  const {
    isPermitted,
    permissionDeniedMessageString,
    _permissionDeniedReason,
    _permissionDeniedMessage,
    _permissionDeniedKey,
    isSuccess,
  } = usePermissions();

  const displayContent = () => {
    // Check if permission is allowed
    if (!isSuccess) {
      return <CircularProgress />;
    } else if (isPermitted(props.keys)) {
      return props.children;
    } else if (
      !isPermitted(props.keys) &&
      _permissionDeniedReason(props.keys) !== undefined &&
      _permissionDeniedKey(props.keys) !== undefined
    ) {
      return _permissionDeniedMessage(props.keys, props.messages);
    } else {
      // Permission is not allowed and there is no reason
      // TODO DS-5166: This is an error state but should not be possible? :shrug:
    }
  };

  return displayContent();
};

// TODO DS-5166: Probably move this somewhere more generic
interface TooltipWrapperProps {
  title: String;
  children: React.ReactNode;
  style?: React.CSSProperties;
}
export const TooltipWrapper = (props: TooltipWrapperProps) => {
  return (
    <Tooltip style={props.style} title={props.title}>
      <span>{props.children}</span>
    </Tooltip>
  );
};
